import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminTable from "components/admin/AdminTable";
import Button from "components/Button";
import { ColumnDef } from "types/admin";
import {
  getNonRevConfigs,
  addNonRevConfig,
  updateNonRevConfig,
  deleteNonRevConfig,
  NonRevConfig,
} from "apis/adminServices/nonRevConfigServices";
import AddNonRevConfigModal from "./AddNonRevConfigModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { toast, ToastContainer } from "react-toastify";
import NonRevAirlinesModal from "./NonRevModal";
import RowsPerPageSelect from "components/admin/RowsPerPageSelect";

// Pagination implemented locally

const NonRevConfiguration: React.FC = () => {
  const [data, setData] = useState<NonRevConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedAirlines, setSelectedAirlines] = useState<{ nonRevAirlineName: string }[]>([]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<NonRevConfig | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getNonRevConfigs(page, pageSize, search);

      // API returns { items: [...], totalCount: 8, page: 1, pageSize: 10 }
      if (response && response.items) {
        setData(response.items);
        setTotalCount(response.totalCount ?? response.items.length);
      } else if (response && Array.isArray(response)) {
        // Fallback if response is directly an array
        setData(response);
        setTotalCount(response.length);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load configurations");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleAdd = () => {
    setEditingConfig(null);
    setIsAddModalOpen(true);
  };

  const handleEdit = (item: NonRevConfig) => {
    setEditingConfig(item);
    setIsAddModalOpen(true);
  };

  const handleDeleteClick = (item: NonRevConfig) => {
    setDeletingId(item.airlineId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deletingId === null) return;
    setDeleteLoading(true);
    try {
      await deleteNonRevConfig(deletingId);
      toast.success("The entry has been successfully deleted.");
      fetchData();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Deletion failed. Please try again.");
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setDeletingId(null);
    }
  };

  const handleFormSubmit = async (airlineId: number, nonRevAirlineIds: number[]) => {
    try {
      if (editingConfig) {
        await updateNonRevConfig({
          airlineId,
          nonRevAirlines: nonRevAirlineIds,
        });
        toast.success("Configuration updated successfully");
      } else {
        await addNonRevConfig({
          airlineId,
          nonRevAirlineIds,
        });
        toast.success("Configuration added successfully");
      }
      fetchData();
      setIsAddModalOpen(false);
      setEditingConfig(null);
    } catch (error) {
      console.error("Submit failed:", error);
      toast.error(editingConfig ? "Failed to update configuration" : "Failed to add configuration");
      throw error;
    }
  };

  const columns: ColumnDef<NonRevConfig>[] = [
    {
      key: "airlineName",
      header: "Flight Name",
    },
    {
      key: "nonRevAirlines",
      header: "Non-rev flights",
      render: (item) => {
        if (!item.nonRevAirlines || item.nonRevAirlines.length === 0) return "-";

        return (
          <span
            className="text-blue-600 font-medium cursor-pointer"
            onClick={() => {
              setSelectedAirlines(item.nonRevAirlines);
              setOpenModal(true);
            }}
          >
            {item.nonRevAirlines.length === 1 ? item.nonRevAirlines[0].nonRevAirlineName : `${item.nonRevAirlines.length} Airlines`}
          </span>
        );
      },
    },
  ];

  // Derive page count from the authoritative totalCount (not from how many rows
  // the current page happens to return, which breaks on exact multiples).
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  // Windowed page numbers (max 5), mirroring the other admin tables.
  const pageNumbers = (() => {
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  })();

  return (
    <>
      <ToastContainer />
      <section className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-grease-100">Non-Rev Configuration</h1>
        <nav className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-lightgray-200" aria-label="Breadcrumb">
          <span className="st-dashboard text-lightgray-200 text-sm" aria-hidden="true" />
          <Link to="/admin/dashboard" className="hover:text-royalblue-100 transition-colors duration-150">
            Dashboard
          </Link>
          <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
          <span className="text-royalblue-100 font-medium">Non-Rev Config</span>
        </nav>
      </section>

      <section className="bg-white p-6 rounded-xl mt-6">
        <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
          <div className="relative w-full md:w-auto md:flex-1 md:max-w-md">
            <input
              type="text"
              placeholder="Search by flight name..."
              className="block w-full p-3 pl-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
              value={search}
              onChange={handleSearch}
            />
            <button className="absolute w-fit inset-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
              <i className="st-search text-lightgray-200"></i>
            </button>
          </div>

          <button
            onClick={handleAdd}
            className="flex items-center justify-center gap-2 p-3 text-sm font-medium bg-[#FFD700] text-white border rounded-lg hover:opacity-90 transition-opacity"
          >
            <span className="st-add text-white" />
            Add New
          </button>
        </div>

        <AdminTable
          columns={columns}
          data={data}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          showSerialNumber={true}
        />

        {/* Pagination Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
          <div className="flex flex-wrap items-center gap-5">
            <p className="text-lightgray-200 text-sm">
              Total {totalCount} {totalCount === 1 ? "record" : "records"}
            </p>
            <p className="text-lightgray-200 text-sm">Rows per page</p>
            <RowsPerPageSelect
              value={pageSize}
              onChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>

          <div>
            <ul className="flex flex-wrap items-center justify-center my-6 gap-3 text-sm">
              {/* Previous Button */}
              <li>
                <button
                  disabled={page === 1}
                  className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                    page === 1 ? "text-gray-300 border-gray-300" : "text-primaryblue-50 border-primaryblue-50"
                  }`}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <span className="sr-only">Previous</span>
                  <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                </button>
              </li>

              {/* Page Numbers (windowed) */}
              {pageNumbers.map((p) => (
                <li key={p}>
                  <button
                    onClick={() => setPage(p)}
                    className={`flex items-center justify-center w-7 h-7 rounded-md border ${
                      p === page
                        ? "text-white border-primaryblue-50 bg-primaryblue-50"
                        : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                  >
                    {p}
                  </button>
                </li>
              ))}

              {/* Next Button */}
              <li>
                <button
                  disabled={page >= totalPages}
                  className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                    page >= totalPages ? "text-gray-300 border-gray-300" : "text-primaryblue-50 border-primaryblue-50"
                  }`}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <span className="sr-only">Next</span>
                  <i className="st-arrow-right text-xl font-semibold"></i>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <AddNonRevConfigModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleFormSubmit}
        initialData={
          editingConfig
            ? {
                airlineId: editingConfig.airlineId,
                nonRevAirlineIds: editingConfig.nonRevAirlines.map((a) => a.nonRevAirlineId),
                baseAirlineName: editingConfig.airlineName,
                nonRevAirlineNames: editingConfig.nonRevAirlines.map((a) => a.nonRevAirlineName),
              }
            : null
        }
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
      />
      <NonRevAirlinesModal open={openModal} onClose={() => setOpenModal(false)} airlines={selectedAirlines} />
    </>
  );
};

export default NonRevConfiguration;
